import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  MouseEvent,
  KeyboardEvent,
  TouchEvent,
} from "react";
import MetricBuilder from "@pages/CROModule/components/CROPage/components/Sections/MetricBuilder";
import { WidgetMetricKey } from "@services/math/math.service";
import { Widget } from "@pages/Dashboard/features/Widgets/Widget";
import styles from "./styles.module.scss";
import classNames from "classnames";
import WidgetIcon from "@assets/icons/widgetsDNDBtn";
import { GroupSkeleton } from "../Skeleton";
import { useWidgets } from "../hooks/useWidgets";

interface IDefaultViewProps {
  choosenWidgets: WidgetMetricKey[];
  showWhatIf: boolean;
  isCRO?: boolean;
  showHistorical?: boolean;
  showOnly?: "COO" | "Market" | "CFO" | "Ad";
  dateRange?: {
    startDate: Date;
    endDate: Date;
  };
  compareType?: "raw" | "percent";
  compareWith?: {
    startDate: Date;
    endDate: Date;
  };
  asin?: string;
}

export const DefaultView: React.FC<IDefaultViewProps> = ({
  choosenWidgets,
  showWhatIf,
  isCRO,
  showHistorical,
  showOnly,
  dateRange,
  compareType,
  compareWith,
  asin,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [targetIndex, setTargetIndex] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const emptyImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const img = new Image();
    img.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
    emptyImageRef.current = img;
  }, []);

  const { widgets, setWidgets, isLoading } = useWidgets({
    selectedWidgets: choosenWidgets,
    dateRange,
    compareWith,
    compareType,
    showHistorical,
    isWhatIf: showWhatIf,
    asin,
  });

  useEffect(() => {
    itemRefs.current = itemRefs.current.slice(0, widgets.length);
  }, [widgets]);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = "move";
    if (emptyImageRef.current) {
      e.dataTransfer.setDragImage(emptyImageRef.current, 0, 0);
    }
    const ghostElement = document.createElement("div");
    ghostElement.className = styles.ghostElement;
    document.body.appendChild(ghostElement);
    ghostRef.current = ghostElement;
    setDraggedIndex(index);
    const draggedItem = itemRefs.current[index];
    if (draggedItem) {
      draggedItem.classList.add(styles.isDragging);
      const rect = draggedItem.getBoundingClientRect();
      ghostElement.style.width = `${rect.width}px`;
      ghostElement.style.height = `${rect.height}px`;
      ghostElement.style.position = "fixed";
      ghostElement.style.top = "0";
      ghostElement.style.left = "0";
      ghostElement.style.pointerEvents = "none";
      ghostElement.style.zIndex = "9999";
      ghostElement.style.transform = `translate(${e.clientX - 100}px, ${e.clientY - 50}px)`;
      ghostElement.innerHTML = draggedItem.innerHTML;

      const ghostHandles = ghostElement.querySelectorAll(
        `.${styles.dragHandle}`,
      );
      ghostHandles.forEach((handle) => handle.remove());
    }
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    if (ghostRef.current && e.clientX > 0 && e.clientY > 0) {
      ghostRef.current.style.transform = `translate(${e.clientX - 100}px, ${e.clientY - 50}px)`;
    }
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (index !== draggedIndex) {
        setTargetIndex(index);
      }
    },
    [draggedIndex],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (index !== draggedIndex) {
        setTargetIndex(index);
      }
    },
    [draggedIndex],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent, index: number) => {
      if (e.currentTarget.contains(e.relatedTarget as Node)) {
        return;
      }

      if (index === targetIndex) {
        setTargetIndex(null);
      }
    },
    [targetIndex],
  );

  const handleDragEnd = useCallback(() => {
    if (ghostRef.current) {
      document.body.removeChild(ghostRef.current);
      ghostRef.current = null;
    }
    if (
      draggedIndex !== null &&
      targetIndex !== null &&
      draggedIndex !== targetIndex
    ) {
      const newWidgets = [...widgets];
      const [draggedWidget] = newWidgets.splice(draggedIndex, 1);
      newWidgets.splice(targetIndex, 0, draggedWidget);
      setWidgets(newWidgets);
    }
    if (draggedIndex !== null && itemRefs.current[draggedIndex]) {
      itemRefs.current[draggedIndex]?.classList.remove(styles.isDragging);
    }
    setDraggedIndex(null);
    setTargetIndex(null);
  }, [draggedIndex, targetIndex, widgets]);

  const defaultView = (
    <div className={styles.container}>
      {isLoading ? (
        <GroupSkeleton count={choosenWidgets.length} />
      ) : (
        <div
          ref={containerRef}
          className={classNames(styles.widgets, { [styles.cro]: isCRO })}
        >
          {widgets.map((widget, index) => (
            <div
              key={widget.id ?? index}
              ref={(el) => (itemRefs.current[index] = el)}
              className={classNames(styles.widgetWrapper, {
                [styles.isTarget]: index === targetIndex,
              })}
              draggable
              role="button"
              tabIndex={0}
              onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleDragStart(e as unknown as React.DragEvent, index);
                }
              }}
              onTouchStart={(e: TouchEvent) =>
                handleDragStart(e as unknown as React.DragEvent, index)
              }
              onTouchMove={(e: TouchEvent) =>
                handleDrag(e as unknown as React.DragEvent)
              }
              onTouchEnd={() => handleDragEnd()}
              onDragStart={(e) => handleDragStart(e, index)}
              onDrag={handleDrag}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragLeave={(e) => handleDragLeave(e, index)}
              onDragEnd={handleDragEnd}
            >
              <div className={styles.dragHandle}>
                {typeof WidgetIcon === "function" ? <WidgetIcon /> : "≡"}
              </div>
              <Widget widget={widget} additionalClass={styles.widget} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const metricBuilderView = (
    <div className={styles.optimisation}>
      <MetricBuilder
        metricPerRow={5}
        useConfigButton
        hideSettings
        hideLine
        isWhatIf={showWhatIf}
        choosenWidgets={choosenWidgets}
        showOnly={showOnly}
      />
    </div>
  );

  if (showOnly === "COO") {
    return defaultView;
  }
  return showWhatIf ? metricBuilderView : defaultView;
};
